/** Frozen contract — admin spine pages import these shapes in phases 3+4. */

export const USER_STATUSES = ['active', 'pending', 'blocked', 'rejected'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const PLATFORM_ROLES = ['admin'] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];

export type AdminUserSummary = {
  id: string;
  email: string;
  name: string | null;
  status: UserStatus;
  platformRole: PlatformRole | null;
  createdAt: number;
  isEnvAdmin: boolean;
  threadCount: number;
  projectCount: number;
  updatedAt?: number | null;
  blockedAt?: number | null;
  blockedReason?: string | null;
};

export type PendingSignupSummary = {
  id: string;
  email: string;
  name: string | null;
  createdAt: number;
};

export type AdminProjectMember = {
  userId: string;
  email: string;
  name: string | null;
  role: 'project_admin' | 'project_member' | 'project_viewer';
};

export type AdminProjectSummary = {
  id: string;
  name: string;
  ownerEmail: string;
  ownerName: string | null;
  memberCount: number;
  createdAt: number;
  members: AdminProjectMember[];
};

export type ConnectorAuthState = 'connected' | 'needs-auth' | 'failed' | 'disabled';

export type AdminConnectorSummary = {
  id: string;
  marketplaceId: string;
  name: string;
  transport: 'stdio' | 'sse' | 'http';
  enabled: boolean;
  authState: ConnectorAuthState;
  installCount: number;
  logoUrl?: string;
  logoInvertInDark?: boolean;
  authType: 'api_key' | 'oauth' | 'oauth_api_key';
  connectedUsers?: { name: string; email: string }[];
  oauthClientLast4?: string;
  oauthClientSetAt?: number;
  oauthClientSetBy?: string;
};

export const SYSTEM_PROMPT_STATUSES = ['draft', 'published', 'archived'] as const;
export type SystemPromptStatus = (typeof SYSTEM_PROMPT_STATUSES)[number];

export const SYSTEM_PROMPT_KITS = ['claude', 'google'] as const;
export type SystemPromptKit = (typeof SYSTEM_PROMPT_KITS)[number];

export const SYSTEM_PROMPT_BODY_MAX_BYTES = 65_536;

export type SystemPromptVersionMeta = {
  id: string;
  kit: SystemPromptKit;
  modelId: string | null;
  name: string | null;
  version: number;
  status: SystemPromptStatus;
  body: string;
  createdAt: number;
  publishedAt: number | null;
  createdByEmail?: string;
};

export type LobbyPanelSurface = 'tasks' | 'docs' | 'chat' | 'data' | 'generic';

export type LobbyPanel = {
  title: string;
  body: string;
  surface: LobbyPanelSurface;
};

/** Verbatim shipped default — matches ai-foundation `admin-system-prompt-shared`. */
export const SHIPPED_SYSTEM_PROMPT_BASE =
  'You are a helpful AI assistant with access to filesystem and shell tools, plus any MCP connectors the user has enabled. You have a sandboxed working directory for any files or commands. Be concise and use Markdown for formatting where it helps.';

export const SEED_LOBBY_PANELS: LobbyPanel[] = [
  {
    title: 'Organise your work',
    body: 'Create projects, assign tasks, and track progress in one place.',
    surface: 'tasks',
  },
  {
    title: 'Chat with your data',
    body: 'Ask questions across docs, connectors, and project files.',
    surface: 'chat',
  },
  {
    title: 'Build with AI',
    body: 'Draft content, summarise meetings, and automate workflows.',
    surface: 'docs',
  },
];

export type AdminDashboardStats = {
  usersTotal: number;
  pendingLobby: number;
  projectsCount: number;
  apiKeysEnabled: number;
  apiKeysTotal: number;
  apiKeysConfigured: number;
  servicesDown: number;
  servicesDegraded: number;
  systemReadiness: 'ok' | 'warming' | 'draining';
};

/** Frozen epoch for admin seed timestamps (2026-07-15 UTC). */
export const ADMIN_SEED_ANCHOR_MS = Date.UTC(2026, 6, 15);

const day = 86_400_000;
const now = ADMIN_SEED_ANCHOR_MS;

export const SEED_ADMIN_USERS: AdminUserSummary[] = [
  {
    id: 'user-alex',
    email: 'alex@example.com',
    name: 'Alex Kim',
    status: 'active',
    platformRole: 'admin',
    createdAt: now - 120 * day,
    isEnvAdmin: true,
    threadCount: 48,
    projectCount: 4,
    updatedAt: now - 2 * day,
  },
  {
    id: 'user-sam',
    email: 'sam.patel@example.com',
    name: 'Sam Patel',
    status: 'active',
    platformRole: null,
    createdAt: now - 95 * day,
    isEnvAdmin: false,
    threadCount: 31,
    projectCount: 2,
    updatedAt: now - 5 * day,
  },
  {
    id: 'user-jordan',
    email: 'jordan.lee@example.com',
    name: 'Jordan Lee',
    status: 'active',
    platformRole: null,
    createdAt: now - 72 * day,
    isEnvAdmin: false,
    threadCount: 22,
    projectCount: 3,
    updatedAt: now - 1 * day,
  },
  {
    id: 'user-morgan',
    email: 'morgan.chen@example.com',
    name: 'Morgan Chen',
    status: 'blocked',
    platformRole: null,
    createdAt: now - 60 * day,
    isEnvAdmin: false,
    threadCount: 8,
    projectCount: 1,
    blockedAt: now - 14 * day,
    blockedReason: 'Policy violation',
    updatedAt: now - 14 * day,
  },
  {
    id: 'user-riley',
    email: 'riley.nguyen@example.com',
    name: 'Riley Nguyen',
    status: 'active',
    platformRole: null,
    createdAt: now - 45 * day,
    isEnvAdmin: false,
    threadCount: 15,
    projectCount: 2,
    updatedAt: now - 3 * day,
  },
  {
    id: 'user-casey',
    email: 'casey.wright@example.com',
    name: 'Casey Wright',
    status: 'rejected',
    platformRole: null,
    createdAt: now - 30 * day,
    isEnvAdmin: false,
    threadCount: 0,
    projectCount: 0,
    updatedAt: now - 30 * day,
  },
  {
    id: 'user-taylor',
    email: 'taylor.brooks@example.com',
    name: 'Taylor Brooks',
    status: 'active',
    platformRole: null,
    createdAt: now - 18 * day,
    isEnvAdmin: false,
    threadCount: 6,
    projectCount: 1,
    updatedAt: now - 4 * day,
  },
  {
    id: 'user-avery',
    email: 'avery.diaz@example.com',
    name: 'Avery Diaz',
    status: 'pending',
    platformRole: null,
    createdAt: now - 5 * day,
    isEnvAdmin: false,
    threadCount: 0,
    projectCount: 0,
    updatedAt: now - 5 * day,
  },
];

export const SEED_PENDING_SIGNUPS: PendingSignupSummary[] = [
  {
    id: 'signup-1',
    email: 'jamie.ortiz@acme.co',
    name: 'Jamie Ortiz',
    createdAt: now - 2 * day,
  },
  {
    id: 'signup-2',
    email: 'drew.hansen@northwind.io',
    name: 'Drew Hansen',
    createdAt: now - 1 * day,
  },
  {
    id: 'signup-3',
    email: 'quinn.morris@studio.example',
    name: null,
    createdAt: now - 6 * 3_600_000,
  },
];

export const SEED_ADMIN_PROJECTS: AdminProjectSummary[] = [
  {
    id: 'proj-foundation',
    name: 'Foundation mock',
    ownerEmail: 'alex@example.com',
    ownerName: 'Alex Kim',
    memberCount: 4,
    createdAt: now - 80 * day,
    members: [
      {
        userId: 'user-alex',
        email: 'alex@example.com',
        name: 'Alex Kim',
        role: 'project_admin',
      },
      {
        userId: 'user-sam',
        email: 'sam.patel@example.com',
        name: 'Sam Patel',
        role: 'project_member',
      },
      {
        userId: 'user-jordan',
        email: 'jordan.lee@example.com',
        name: 'Jordan Lee',
        role: 'project_member',
      },
      {
        userId: 'user-riley',
        email: 'riley.nguyen@example.com',
        name: 'Riley Nguyen',
        role: 'project_viewer',
      },
    ],
  },
  {
    id: 'proj-onboarding',
    name: 'Client onboarding',
    ownerEmail: 'sam.patel@example.com',
    ownerName: 'Sam Patel',
    memberCount: 2,
    createdAt: now - 40 * day,
    members: [
      {
        userId: 'user-sam',
        email: 'sam.patel@example.com',
        name: 'Sam Patel',
        role: 'project_admin',
      },
      {
        userId: 'user-taylor',
        email: 'taylor.brooks@example.com',
        name: 'Taylor Brooks',
        role: 'project_member',
      },
    ],
  },
  {
    id: 'proj-research',
    name: 'Research archive',
    ownerEmail: 'jordan.lee@example.com',
    ownerName: 'Jordan Lee',
    memberCount: 2,
    createdAt: now - 22 * day,
    members: [
      {
        userId: 'user-jordan',
        email: 'jordan.lee@example.com',
        name: 'Jordan Lee',
        role: 'project_admin',
      },
      {
        userId: 'user-riley',
        email: 'riley.nguyen@example.com',
        name: 'Riley Nguyen',
        role: 'project_member',
      },
    ],
  },
];

export const SEED_ADMIN_CONNECTORS: AdminConnectorSummary[] = [
  {
    id: 'conn-github',
    marketplaceId: 'github',
    name: 'GitHub',
    transport: 'http',
    enabled: true,
    authState: 'connected',
    installCount: 12,
    logoUrl: '/connector-logos/github.svg',
    logoInvertInDark: true,
    authType: 'oauth',
    connectedUsers: [
      { name: 'Alex Kim', email: 'alex@example.com' },
      { name: 'Sam Patel', email: 'sam.patel@example.com' },
    ],
    oauthClientLast4: 'X7k9',
    oauthClientSetAt: now - 42 * day,
    oauthClientSetBy: 'alex@example.com',
  },
  {
    id: 'conn-notion',
    marketplaceId: 'notion',
    name: 'Notion',
    transport: 'http',
    enabled: true,
    authState: 'needs-auth',
    installCount: 8,
    logoUrl: '/connector-logos/notion.svg',
    logoInvertInDark: true,
    authType: 'oauth',
    connectedUsers: [{ name: 'Jordan Lee', email: 'jordan.lee@example.com' }],
    oauthClientLast4: 'm2Qp',
    oauthClientSetAt: now - 18 * day,
    oauthClientSetBy: 'sam.patel@example.com',
  },
  {
    id: 'conn-linear',
    marketplaceId: 'linear',
    name: 'Linear',
    transport: 'sse',
    enabled: true,
    authState: 'connected',
    installCount: 6,
    logoUrl: '/connector-logos/linear.svg',
    authType: 'oauth_api_key',
    connectedUsers: [{ name: 'Riley Nguyen', email: 'riley.nguyen@example.com' }],
    oauthClientLast4: '9f3A',
    oauthClientSetAt: now - 7 * day,
    oauthClientSetBy: 'alex@example.com',
  },
  {
    id: 'conn-granola',
    marketplaceId: 'granola',
    name: 'Granola',
    transport: 'stdio',
    enabled: false,
    authState: 'disabled',
    installCount: 3,
    authType: 'api_key',
    connectedUsers: [],
  },
  {
    id: 'conn-slack',
    marketplaceId: 'slack',
    name: 'Slack',
    transport: 'http',
    enabled: true,
    authState: 'failed',
    installCount: 5,
    authType: 'oauth',
    connectedUsers: [{ name: 'Taylor Brooks', email: 'taylor.brooks@example.com' }],
    oauthClientLast4: 'p4R1',
    oauthClientSetAt: now - 3 * day,
    oauthClientSetBy: 'jordan.lee@example.com',
  },
  {
    id: 'conn-filesystem',
    marketplaceId: 'filesystem',
    name: 'Filesystem',
    transport: 'stdio',
    enabled: true,
    authState: 'connected',
    installCount: 14,
    authType: 'api_key',
    connectedUsers: [
      { name: 'Alex Kim', email: 'alex@example.com' },
      { name: 'Sam Patel', email: 'sam.patel@example.com' },
      { name: 'Jordan Lee', email: 'jordan.lee@example.com' },
    ],
  },
];

export const SEED_SYSTEM_PROMPT_VERSIONS: SystemPromptVersionMeta[] = [
  {
    id: 'spv-4',
    kit: 'claude',
    modelId: null,
    name: 'Default assistant',
    version: 4,
    status: 'published',
    body: SHIPPED_SYSTEM_PROMPT_BASE,
    createdAt: now - 14 * day,
    publishedAt: now - 13 * day,
    createdByEmail: 'alex@example.com',
  },
  {
    id: 'spv-5-draft',
    kit: 'claude',
    modelId: 'claude-sonnet-4-6',
    name: 'Sonnet scoped',
    version: 5,
    status: 'draft',
    body: 'You are a specialised assistant for Sonnet deployments. Prioritise structured answers.',
    createdAt: now - 3 * day,
    publishedAt: null,
    createdByEmail: 'alex@example.com',
  },
  {
    id: 'spv-3-sonnet-live',
    kit: 'claude',
    modelId: 'claude-sonnet-4-6',
    name: 'Sonnet scoped',
    version: 3,
    status: 'published',
    body: 'You are a helpful assistant tuned for Claude Sonnet.',
    createdAt: now - 20 * day,
    publishedAt: now - 19 * day,
    createdByEmail: 'alex@example.com',
  },
  {
    id: 'spv-2-sonnet-arch',
    kit: 'claude',
    modelId: 'claude-sonnet-4-6',
    name: 'Sonnet scoped',
    version: 2,
    status: 'archived',
    body: 'You are a helpful assistant for Sonnet (archived).',
    createdAt: now - 45 * day,
    publishedAt: now - 44 * day,
    createdByEmail: 'alex@example.com',
  },
  {
    id: 'spv-legacy-disabled',
    kit: 'claude',
    modelId: 'claude-3-opus-legacy',
    name: 'Legacy Opus 3',
    version: 1,
    status: 'published',
    body: 'You are a helpful assistant for the retired Opus 3 endpoint.',
    createdAt: now - 120 * day,
    publishedAt: now - 118 * day,
    createdByEmail: 'alex@example.com',
  },
  {
    id: 'spv-2-google',
    kit: 'google',
    modelId: null,
    name: 'Google kit default',
    version: 2,
    status: 'published',
    body: 'You are a helpful assistant for Google Gemini models in the Foundation mock.',
    createdAt: now - 30 * day,
    publishedAt: now - 28 * day,
    createdByEmail: 'alex@example.com',
  },
  {
    id: 'spv-1-arch',
    kit: 'claude',
    modelId: null,
    name: 'Legacy base',
    version: 1,
    status: 'archived',
    body: 'You are a helpful assistant.',
    createdAt: now - 90 * day,
    publishedAt: now - 88 * day,
    createdByEmail: 'alex@example.com',
  },
];

export const SEED_ADMIN_DASHBOARD_STATS: AdminDashboardStats = {
  usersTotal: SEED_ADMIN_USERS.length,
  pendingLobby: SEED_PENDING_SIGNUPS.length,
  projectsCount: SEED_ADMIN_PROJECTS.length,
  apiKeysEnabled: 2,
  apiKeysTotal: 3,
  apiKeysConfigured: 2,
  servicesDown: 0,
  servicesDegraded: 1,
  systemReadiness: 'ok',
};

export const SEED_ADMIN_USER_STATUS_COUNTS = {
  active: SEED_ADMIN_USERS.filter((u) => u.status === 'active').length,
  pending: SEED_ADMIN_USERS.filter((u) => u.status === 'pending').length,
  blocked: SEED_ADMIN_USERS.filter((u) => u.status === 'blocked').length,
  rejected: SEED_ADMIN_USERS.filter((u) => u.status === 'rejected').length,
} as const;

// — C1 admin config scenes (frozen, deterministic) —

export type WorkspaceConfigSource = 'db' | 'env' | 'default';

export type WorkspaceConfig = {
  enabled: boolean;
  acceptArbitraryUploads: boolean;
  source?: WorkspaceConfigSource;
};

export const SEED_WORKSPACE_CONFIG: WorkspaceConfig = {
  enabled: true,
  acceptArbitraryUploads: false,
  source: 'db',
};

export type AgentReviewInlineConfig = { enabled: boolean };

export const SEED_AGENT_REVIEW_INLINE_CONFIG: AgentReviewInlineConfig = {
  enabled: true,
};

export type EditorPageFlowConfig = { enabled: boolean };

export const SEED_EDITOR_PAGE_FLOW_CONFIG: EditorPageFlowConfig = {
  enabled: true,
};

export type PredefinedSkillDef = {
  slug: string;
  name?: string;
  description: string;
  body: string;
  enabled: boolean;
};

export type PredefinedSkillsManifest = { skills: PredefinedSkillDef[] };

export const SEED_PREDEFINED_SKILLS_MANIFEST: PredefinedSkillsManifest = {
  skills: [
    {
      slug: 'summarize',
      name: 'Summarize',
      description: 'Condense long documents into bullet summaries.',
      body: '# Summarize\n\nProduce concise bullet summaries of long inputs.',
      enabled: true,
    },
    {
      slug: 'code-review',
      name: 'Code review',
      description: 'Structured review checklist for pull requests.',
      body: '# Code review\n\nRun a structured PR review checklist.',
      enabled: true,
    },
    {
      slug: 'meeting-notes',
      name: 'Meeting notes',
      description: 'Turn transcripts into action items.',
      body: '# Meeting notes\n\nExtract decisions and action items from transcripts.',
      enabled: false,
    },
  ],
};

export type EmbeddingProviderId = 'voyage' | 'gemini' | 'bedrock';

export type EmbeddingProviderLoadResult = {
  active: {
    provider: EmbeddingProviderId;
    model: string;
    tag: string;
    source: 'admin' | 'env';
  };
  columnDim: number;
  keyPresence: Record<EmbeddingProviderId, boolean>;
  staleMemoryVectors: number;
  staleProjectFileVectors: number;
  reembed: {
    running: boolean;
    startedAt: number | null;
    processed: number;
    embedded: number;
    failed: number;
  };
  bedrockStatus: {
    credentialPresent: boolean;
    region: string;
    regionSource: 'admin' | 'env' | 'default';
  };
};

export const SEED_EMBEDDING_PROVIDER_LOAD: EmbeddingProviderLoadResult = {
  active: {
    provider: 'voyage',
    model: 'voyage-3',
    tag: 'voyage:voyage-3',
    source: 'admin',
  },
  columnDim: 1024,
  keyPresence: { voyage: true, gemini: false, bedrock: true },
  staleMemoryVectors: 1240,
  staleProjectFileVectors: 3890,
  reembed: {
    running: false,
    startedAt: null,
    processed: 0,
    embedded: 0,
    failed: 0,
  },
  bedrockStatus: {
    credentialPresent: true,
    region: 'us-east-1',
    regionSource: 'env',
  },
};

export type SandboxPythonPackage = { name: string; version: string };

export type SandboxDesiredManifest = {
  python: SandboxPythonPackage[];
  apt: string[];
  notes: string;
};

export type SandboxDeployedRecord = {
  snapshotId: string;
  builtAt: number;
  digest: string;
  manifestHash: string;
};

export type SandboxLibrariesLoadResult = {
  desired: SandboxDesiredManifest;
  deployed: SandboxDeployedRecord | null;
  drift: boolean;
};

export const SEED_SANDBOX_DESIRED_MANIFEST: SandboxDesiredManifest = {
  python: [
    { name: 'numpy', version: '2.1.0' },
    { name: 'pandas', version: '2.2.2' },
    { name: 'requests', version: '2.32.3' },
  ],
  apt: ['libreoffice'],
  notes: 'Foundation mock bake notes.',
};

export const SEED_SANDBOX_LIBRARIES_LOAD: SandboxLibrariesLoadResult = {
  desired: SEED_SANDBOX_DESIRED_MANIFEST,
  deployed: {
    snapshotId: 'snap-foundation-mock',
    builtAt: ADMIN_SEED_ANCHOR_MS,
    digest: 'sha256:foundation-mock-digest',
    manifestHash: 'a1b2c3d4e5f6789012345678abcdef9012345678abcdef9012345678abcdef90',
  },
  drift: false,
};

// — Service health (ADM-4.2) —

export const PROBE_STALE_MS = 24 * 60 * 60 * 1000;

export const SERVICE_HEALTH_SERVICES = ['daytona', 'claude', 'google'] as const;
export type ServiceHealthServiceId = (typeof SERVICE_HEALTH_SERVICES)[number];
export type ServiceHealthStatus = 'up' | 'degraded' | 'down';
export type ServiceHealthKeyStatus = 'configured' | 'env' | 'missing';

export type ServiceHealthProbeSnapshot = {
  status: 'ok' | 'error' | 'unknown';
  checkedAt: number;
  errorCode: string | null;
  errorMessage: string | null;
};

export type ServiceHealthDetails =
  | { kind: 'daytona'; apiHost: string; sandboxBackend: string }
  | { kind: 'claude'; sandboxBackend: string; bedrockModeActive: boolean }
  | { kind: 'google'; maxLlmCalls: number; sandboxBackend: string };

export type ServiceHealthRow = {
  service: ServiceHealthServiceId;
  status: ServiceHealthStatus;
  keyStatus: ServiceHealthKeyStatus;
  kitEnabled: boolean;
  probe: ServiceHealthProbeSnapshot | null;
  probeStale: boolean;
  userImpact: string;
  details: ServiceHealthDetails;
};

const probeOk = (checkedAt: number): ServiceHealthProbeSnapshot => ({
  status: 'ok',
  checkedAt,
  errorCode: null,
  errorMessage: null,
});

export const SEED_SERVICE_HEALTH_ROWS: ServiceHealthRow[] = [
  {
    service: 'daytona',
    status: 'up',
    keyStatus: 'configured',
    kitEnabled: true,
    probe: probeOk(ADMIN_SEED_ANCHOR_MS - 12 * 60_000),
    probeStale: false,
    userImpact: '',
    details: {
      kind: 'daytona',
      apiHost: 'https://app.daytona.io/api',
      sandboxBackend: 'daytona',
    },
  },
  {
    service: 'claude',
    status: 'degraded',
    keyStatus: 'env',
    kitEnabled: true,
    probe: {
      status: 'error',
      checkedAt: ADMIN_SEED_ANCHOR_MS - 26 * 60 * 60_000,
      errorCode: 'AUTH',
      errorMessage: '401 from Anthropic — key may have rotated',
    },
    probeStale: true,
    userImpact: 'Chat turns may fail until the key is refreshed.',
    details: {
      kind: 'claude',
      sandboxBackend: 'claude-agent-sdk',
      bedrockModeActive: false,
    },
  },
  {
    service: 'google',
    status: 'up',
    keyStatus: 'configured',
    kitEnabled: false,
    probe: probeOk(ADMIN_SEED_ANCHOR_MS - 4 * 60_000),
    probeStale: false,
    userImpact: '',
    details: {
      kind: 'google',
      maxLlmCalls: 50,
      sandboxBackend: 'google-adk',
    },
  },
];

// — System status —

export type RegistrySwitchDefault = 'default';

export type AllowlistSwitchSummary =
  | { allowAll: true }
  | { allowAll: false; count: number };

export type AdminSystemStatusRegistrySwitches = {
  signupLobby: RegistrySwitchDefault | { enabled: boolean };
  allowedConnectors: RegistrySwitchDefault | AllowlistSwitchSummary;
  allowedClaudeModels: RegistrySwitchDefault | AllowlistSwitchSummary;
  allowedGoogleModels: RegistrySwitchDefault | AllowlistSwitchSummary;
  builtinToolsBaseline:
    | RegistrySwitchDefault
    | { alwaysOnCount: number; alwaysOffCount: number };
};

export type AdminSystemStatusGcVec = {
  lastRunAt: number | null;
  runsLastNDays: number;
  orphansLastNDays: number;
};

export type AdminSystemStatusGcSandbox = {
  lastRunAt: number | null;
  runsLastNDays: number;
  hibernatedLastNDays: number;
  destroyedLastNDays: number;
};

export type AdminSystemStatusSnapshot = {
  readiness: {
    status: 'ok' | 'warming' | 'draining';
    db: 'ready' | 'pending';
    listen: 'ready' | 'pending' | 'n/a';
    uptime_ms: number;
    preview_toolchain: {
      soffice: 'ok' | 'unavailable';
      shell: 'ok' | 'unavailable';
    };
    storage: 'ready' | 'unreachable' | 'n/a';
    readyForTraffic: boolean;
  };
  db: {
    dialect: 'sqlite' | 'postgres';
    pool: 'ready' | 'pending';
  };
  telemetry: {
    lastAxiomErrorAt: number | null;
    lastPostHogErrorAt: number | null;
    axiomDroppedFieldCount: number;
    orphanExpiredCount: number;
    skillsScratchReclaimedCount: number;
  };
  gc: {
    vecGc: AdminSystemStatusGcVec;
    sandboxSweeper: AdminSystemStatusGcSandbox;
  };
  deploy: {
    version: string;
    buildId: string | null;
    nodeVersion: string;
    uptime_ms: number;
  };
  registrySwitches: AdminSystemStatusRegistrySwitches;
};

export type VecGcSweepCounters = {
  scanned: number;
  orphans: number;
  purged: number;
  durationMs: number;
};

export type SandboxGcSweepCounters = {
  hibernated: number;
  destroyed: number;
  errors: number;
  durationMs: number;
};

export const SEED_SYSTEM_STATUS: AdminSystemStatusSnapshot = {
  readiness: {
    status: 'ok',
    db: 'ready',
    listen: 'ready',
    uptime_ms: 3 * 24 * 60 * 60_000 + 4 * 60 * 60_000,
    preview_toolchain: { soffice: 'ok', shell: 'ok' },
    storage: 'ready',
    readyForTraffic: true,
  },
  db: { dialect: 'sqlite', pool: 'ready' },
  telemetry: {
    lastAxiomErrorAt: null,
    lastPostHogErrorAt: ADMIN_SEED_ANCHOR_MS - 2 * 24 * 60 * 60_000,
    axiomDroppedFieldCount: 0,
    orphanExpiredCount: 14,
    skillsScratchReclaimedCount: 6,
  },
  gc: {
    vecGc: {
      lastRunAt: ADMIN_SEED_ANCHOR_MS - 18 * 60 * 60_000,
      runsLastNDays: 4,
      orphansLastNDays: 28,
    },
    sandboxSweeper: {
      lastRunAt: ADMIN_SEED_ANCHOR_MS - 6 * 60 * 60_000,
      runsLastNDays: 7,
      hibernatedLastNDays: 12,
      destroyedLastNDays: 3,
    },
  },
  deploy: {
    version: 'foundation-mock',
    buildId: 'b95022f55cafe',
    nodeVersion: 'v22.14.0',
    uptime_ms: 3 * 24 * 60 * 60_000 + 4 * 60 * 60_000,
  },
  registrySwitches: {
    signupLobby: { enabled: true },
    allowedConnectors: { allowAll: false, count: 4 },
    allowedClaudeModels: 'default',
    allowedGoogleModels: { allowAll: false, count: 2 },
    builtinToolsBaseline: { alwaysOnCount: 18, alwaysOffCount: 4 },
  },
};

// — API keys —

export const API_KEY_PROVIDERS = [
  'claude',
  'google',
  'voyage',
  'daytona',
  'bedrock',
  'bedrock-embeddings',
] as const;
export type ApiKeyProvider = (typeof API_KEY_PROVIDERS)[number];

export type ApiKeyStatus = {
  provider: ApiKeyProvider;
  source: 'db' | 'env' | 'none';
  last4: string | null;
  label: string | null;
  rotatedAt: number | null;
  setByEmail: string | null;
};

export type ProbeResult = {
  status: 'ok' | 'error' | 'unknown';
  checkedAt: number;
  errorCode: string | null;
  errorMessage: string | null;
};

export type DaytonaUrlStatus = {
  url: string;
  source: 'db' | 'env' | 'default';
};

export type ClaudeProviderMode =
  | 'anthropic-native'
  | 'aws-bedrock'
  | 'claude-platform-on-aws';

export type ClaudeProviderModeStatus = {
  mode: ClaudeProviderMode;
  source: 'db' | 'default';
};

export type ClaudeAnthropicAwsConfig = {
  workspaceId: string | null;
  region: string;
};

export type BedrockModelHealthEntry = {
  modelId: string;
  profileId: string;
  status: 'ok' | 'error';
  errorCode: string | null;
  errorMessage: string | null;
};

export type BedrockModelHealth = {
  checkedAt: number;
  models: BedrockModelHealthEntry[];
};

export type ApiKeysLoadResult = {
  statuses: ApiKeyStatus[];
  probeResults: Partial<Record<ApiKeyProvider, ProbeResult>>;
  daytonaUrl?: DaytonaUrlStatus;
  claudeProviderMode?: ClaudeProviderModeStatus;
  bedrockModelHealth: BedrockModelHealth | null;
  anthropicAwsConfig?: ClaudeAnthropicAwsConfig;
  anthropicAwsProbe?: ProbeResult | null;
};

export const SEED_API_KEYS_LOAD: ApiKeysLoadResult = {
  statuses: [
    {
      provider: 'claude',
      source: 'db',
      last4: '7a2f',
      label: 'Primary Anthropic',
      rotatedAt: ADMIN_SEED_ANCHOR_MS - 9 * 24 * 60 * 60_000,
      setByEmail: 'alex@example.com',
    },
    {
      provider: 'google',
      source: 'env',
      last4: null,
      label: null,
      rotatedAt: null,
      setByEmail: null,
    },
    {
      provider: 'voyage',
      source: 'db',
      last4: '91bc',
      label: 'Embeddings',
      rotatedAt: ADMIN_SEED_ANCHOR_MS - 40 * 24 * 60 * 60_000,
      setByEmail: 'alex@example.com',
    },
    {
      provider: 'daytona',
      source: 'db',
      last4: 'c4e1',
      label: null,
      rotatedAt: ADMIN_SEED_ANCHOR_MS - 3 * 24 * 60 * 60_000,
      setByEmail: 'alex@example.com',
    },
    {
      provider: 'bedrock',
      source: 'db',
      last4: 'f09a',
      label: 'Bedrock bearer',
      rotatedAt: ADMIN_SEED_ANCHOR_MS - 2 * 24 * 60 * 60_000,
      setByEmail: 'alex@example.com',
    },
    {
      provider: 'bedrock-embeddings',
      source: 'none',
      last4: null,
      label: null,
      rotatedAt: null,
      setByEmail: null,
    },
  ],
  probeResults: {
    claude: {
      status: 'ok',
      checkedAt: ADMIN_SEED_ANCHOR_MS - 20 * 60_000,
      errorCode: null,
      errorMessage: null,
    },
    google: {
      status: 'ok',
      checkedAt: ADMIN_SEED_ANCHOR_MS - 35 * 60_000,
      errorCode: null,
      errorMessage: null,
    },
    voyage: {
      status: 'ok',
      checkedAt: ADMIN_SEED_ANCHOR_MS - 2 * 60 * 60_000,
      errorCode: null,
      errorMessage: null,
    },
    daytona: {
      status: 'ok',
      checkedAt: ADMIN_SEED_ANCHOR_MS - 11 * 60_000,
      errorCode: null,
      errorMessage: null,
    },
    bedrock: {
      status: 'ok',
      checkedAt: ADMIN_SEED_ANCHOR_MS - 45 * 60_000,
      errorCode: null,
      errorMessage: null,
    },
  },
  daytonaUrl: {
    url: 'https://app.daytona.io/api',
    source: 'db',
  },
  claudeProviderMode: {
    mode: 'anthropic-native',
    source: 'db',
  },
  bedrockModelHealth: {
    checkedAt: ADMIN_SEED_ANCHOR_MS - 45 * 60_000,
    models: [
      {
        modelId: 'claude-sonnet-4-6',
        profileId: 'us.anthropic.claude-sonnet-4-6',
        status: 'ok',
        errorCode: null,
        errorMessage: null,
      },
      {
        modelId: 'claude-opus-4-8',
        profileId: 'us.anthropic.claude-opus-4-8',
        status: 'ok',
        errorCode: null,
        errorMessage: null,
      },
    ],
  },
  anthropicAwsConfig: {
    workspaceId: 'wrkspc_mock_foundation',
    region: 'us-east-1',
  },
  anthropicAwsProbe: {
    status: 'ok',
    checkedAt: ADMIN_SEED_ANCHOR_MS - 3 * 60 * 60_000,
    errorCode: null,
    errorMessage: null,
  },
};

// — C3 admin panels —

export type SsoOidcStatus = {
  provider: string;
  source: 'db' | 'env' | 'none';
  tenantId: string | null;
  last4: string | null;
  rotatedAt: number | null;
  setByEmail: string | null;
};

export const SEED_SSO_STATUS: SsoOidcStatus = {
  provider: 'microsoft-entra',
  source: 'db',
  tenantId: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
  last4: '7x9k',
  rotatedAt: ADMIN_SEED_ANCHOR_MS - 45 * day,
  setByEmail: 'alex@example.com',
};

export type MaintenanceConfig = {
  enabled: boolean;
  message: string;
  expectedEnd: string | null;
  supportEmail: string | null;
  engagesAt: string | null;
};

export const SEED_MAINTENANCE_SERVER_NOW = new Date(ADMIN_SEED_ANCHOR_MS).toISOString();

export const SEED_MAINTENANCE_CONFIG: MaintenanceConfig = {
  enabled: false,
  message:
    'We are performing a scheduled database upgrade. We expect to be back in approximately 2 hours.',
  expectedEnd: new Date(ADMIN_SEED_ANCHOR_MS + 4 * 60 * 60_000).toISOString(),
  supportEmail: 'support@example.com',
  engagesAt: new Date(ADMIN_SEED_ANCHOR_MS + 2 * 60 * 60_000).toISOString(),
};

export type AnnouncementBannerConfig = {
  enabled: boolean;
  message: string;
  updatedAt: string;
};

export const SEED_ANNOUNCEMENT_BANNER_CONFIG: AnnouncementBannerConfig = {
  enabled: false,
  message: 'We will be performing maintenance on Saturday from 2–4 am UTC.',
  updatedAt: new Date(ADMIN_SEED_ANCHOR_MS).toISOString(),
};

export type KitAllowlistSeed = {
  allowedIds: string[] | null;
  kitEnabled: boolean;
  maxLlmCalls?: number | null;
};

export type AiModelsSeed = {
  platformDefaultModelId: string | null;
  appDefaultModelId: string;
  allModels: Array<{
    id: string;
    label: string;
    shortLabel: string;
    description: string;
    kit: 'claude' | 'google';
    contextWindowTokens: number;
  }>;
  webSearch: { claude: boolean; google: boolean };
  claude: KitAllowlistSeed;
  google: KitAllowlistSeed;
  modelHealth?: Map<string, BedrockModelHealthEntry>;
};

export const SEED_AI_MODELS: AiModelsSeed = {
  platformDefaultModelId: 'claude-sonnet-4-6',
  appDefaultModelId: 'claude-opus-4-8',
  allModels: [
    {
      id: 'claude-opus-4-8',
      label: 'Claude Opus 4.8',
      shortLabel: 'Opus 4.8',
      description: 'Most capable. Best for complex reasoning and long-running tasks.',
      kit: 'claude',
      contextWindowTokens: 1_000_000,
    },
    {
      id: 'claude-sonnet-4-6',
      label: 'Claude Sonnet 4.6',
      shortLabel: 'Sonnet 4.6',
      description: 'Balanced speed and intelligence. Good default.',
      kit: 'claude',
      contextWindowTokens: 200_000,
    },
    {
      id: 'claude-haiku-4-5',
      label: 'Claude Haiku 4.5',
      shortLabel: 'Haiku 4.5',
      description: 'Fastest and cheapest. Best for simple turns.',
      kit: 'claude',
      contextWindowTokens: 200_000,
    },
    {
      id: 'gemini-3.5-flash',
      label: 'Gemini 3.5 Flash',
      shortLabel: 'Gemini Flash',
      description: 'Fast Gemini model via Google ADK (feature-flagged).',
      kit: 'google',
      contextWindowTokens: 1_048_576,
    },
    {
      id: 'gemini-3.1-pro-preview',
      label: 'Gemini 3.1 Pro',
      shortLabel: 'Gemini Pro',
      description: 'Capable Gemini model via Google ADK (feature-flagged).',
      kit: 'google',
      contextWindowTokens: 2_097_152,
    },
    {
      id: 'gemini-3.1-flash-lite',
      label: 'Gemini 3.1 Flash-Lite',
      shortLabel: 'Gemini Flash-Lite',
      description:
        'Most cost-efficient — high-volume agentic tasks and simple processing (via Google ADK, feature-flagged).',
      kit: 'google',
      contextWindowTokens: 1_048_576,
    },
  ],
  webSearch: { claude: true, google: true },
  claude: { allowedIds: null, kitEnabled: true },
  google: { allowedIds: null, kitEnabled: true, maxLlmCalls: 50 },
  modelHealth: new Map([
    [
      'claude-sonnet-4-6',
      {
        modelId: 'claude-sonnet-4-6',
        profileId: 'us.anthropic.claude-sonnet-4-6',
        status: 'ok',
        errorCode: null,
        errorMessage: null,
      },
    ],
    [
      'gemini-3.5-flash',
      {
        modelId: 'gemini-3.5-flash',
        profileId: 'gemini-3.5-flash',
        status: 'ok',
        errorCode: null,
        errorMessage: null,
      },
    ],
  ]),
};

export type BuiltinToolsSeed = {
  alwaysOn: string[];
  alwaysOff: string[];
  coreFamiliesDisabled: string[];
  appMcpSubfamiliesDisabled: string[];
  appMcpSubfamilyToolCounts?: Record<string, number>;
};

export const SEED_BUILTIN_TOOLS: BuiltinToolsSeed = {
  alwaysOn: [],
  alwaysOff: [],
  coreFamiliesDisabled: [],
  appMcpSubfamiliesDisabled: [],
  appMcpSubfamilyToolCounts: {
    'platform.skills': 12,
    'platform.projects': 8,
    'platform.documents': 6,
    'platform.library': 5,
    'platform.memory': 4,
    'platform.workflow': 7,
  },
};
